/**
 * scent-filter.js
 *
 * Client-side scent filtering for collection pages.
 *
 * Works in two modes:
 *
 * 1. Highlights mode (Colour Me, Perfumers Choice)
 *    - Section wrapper has [data-scent-filter-section]
 *    - Highlight buttons carry [data-scent-filter="vanilla"]
 *    - Active state uses .is-active class
 *
 * 2. Essentials mode
 *    - Same [data-scent-filter-section] on the filter bar
 *    - Scent pills carry [data-scent-filter="vanilla"]
 *    - "Best Sellers" clear button carries [data-scent-filter-clear]
 *    - Active state uses .essentials-scent-pill--active on pills
 *      and .essentials-filter-label--active on the clear button
 *
 * Product cards carry [data-grid-item] and [data-notes="vanilla,woody,citrus,"]
 */

(function () {
  'use strict';

  const HIDDEN_ATTR = 'data-scent-hidden';

  // Placeholder values that mean "no data" — treat as untagged
  var IGNORED_NOTES = ['see perfume notes', 'see product notes', 'various'];

  function init() {
    const filterSections = document.querySelectorAll('[data-scent-filter-section]');
    if (!filterSections.length) return;

    filterSections.forEach(function (section) {
      const scentButtons = Array.from(section.querySelectorAll('[data-scent-filter]'));
      const clearButton  = section.querySelector('[data-scent-filter-clear]');

      if (!scentButtons.length) return;

      // Detect which mode we're in
      const isEssentials = !!clearButton;

      const gridScope = findGridScope(section);

      const preActiveBtn = scentButtons.filter(function (b) {
        return isEssentials
          ? b.classList.contains('essentials-scent-pill--active')
          : b.classList.contains('is-active');
      })[0];
      if (preActiveBtn) {
        filterByScent(gridScope, preActiveBtn.getAttribute('data-scent-filter'), section, isEssentials);
      }

      // ── Scent pill clicks ──
      // Delegated on the section (rather than bound per-button) so pills
      // added or changed after this runs are still clickable.
      section.addEventListener('click', function (event) {
        const btn = event.target.closest('[data-scent-filter]');
        if (!btn || !section.contains(btn)) return;

        const currentButtons = Array.from(section.querySelectorAll('[data-scent-filter]'));
        const scent    = btn.getAttribute('data-scent-filter');
        const isActive = isEssentials
          ? btn.classList.contains('essentials-scent-pill--active')
          : btn.classList.contains('is-active');

        // Clear all pill active states
        clearAllActive(currentButtons, clearButton, isEssentials);

        if (isActive) {
          // Toggle off — show everything, restore "Best Sellers" as active
          showAll(gridScope);
          if (isEssentials && clearButton) {
            clearButton.classList.add('essentials-filter-label--active');
            clearButton.setAttribute('aria-pressed', 'true');
          }
        } else {
          // Activate this pill
          if (isEssentials) {
            btn.classList.add('essentials-scent-pill--active');
          } else {
            btn.classList.add('is-active');
          }
          btn.setAttribute('aria-pressed', 'true');
          filterByScent(gridScope, scent, section, isEssentials);
        }
      });

      // ── "Best Sellers" clear button (essentials only) ──
      if (clearButton) {
        clearButton.addEventListener('click', function () {
          clearAllActive(scentButtons, clearButton, isEssentials);
          showAll(gridScope);
          clearButton.classList.add('essentials-filter-label--active');
          clearButton.setAttribute('aria-pressed', 'true');
        });
      }
    });
  }

  function findGridScope(section) {
    let node = section;
    while (node && node !== document.body) {
      if (node.querySelectorAll('[data-grid-item]').length) return node;
      node = node.parentElement;
    }
    return document;
  }

  function clearAllActive(scentButtons, clearButton, isEssentials) {
    (scentButtons || []).forEach(function (b) {
      if (isEssentials) {
        b.classList.remove('essentials-scent-pill--active');
      } else {
        b.classList.remove('is-active');
      }
      b.setAttribute('aria-pressed', 'false');
    });
    if (clearButton) {
      clearButton.classList.remove('essentials-filter-label--active');
      clearButton.setAttribute('aria-pressed', 'false');
    }
  }

  function getCards(scope) {
    return (scope || document).querySelectorAll('[data-grid-item]');
  }

  function matchesScent(note, scent) {
    if (!scent || !note) return false;
    const n = note.toLowerCase().trim();
    const s = scent.toLowerCase().trim();
    if (n === s) return true;
    const words = n.split(/[\s/,\-_]+/);
    if (words.indexOf(s) !== -1) return true;
    if (n.indexOf(s) !== -1) return true;
    return false;
  }

  function removeEmptyState(scope) {
    const root = scope || document;
    root.querySelectorAll('.scent-filter-no-results').forEach(function (el) {
      el.remove();
    });
  }

  function showEmptyState(scope, section, isEssentials) {
    removeEmptyState(scope);

    const cards = getCards(scope);
    if (!cards.length) return;

    const container = cards[0].parentElement;
    if (!container) return;

    const noResults = document.createElement('div');
    noResults.className = 'no-results scent-filter-no-results';
    noResults.setAttribute('role', 'status');
    noResults.setAttribute('aria-live', 'polite');
    noResults.style.cssText = 'grid-column: 1 / -1; width: 100%; text-align: center; padding: 60px 20px;';
    noResults.innerHTML = [
      '<p style="margin-bottom: 20px;"><strong style="font-size: 1.125rem;">No products found for this fragrance family.</strong></p>',
      '<button type="button" class="btn caps btn--primary btn--solid" data-scent-clear-results style="margin-top: 10px; cursor: pointer;">',
      '  <span>Clear filter</span>',
      '</button>'
    ].join('');

    container.appendChild(noResults);

    const clearResultsBtn = noResults.querySelector('[data-scent-clear-results]');
    if (clearResultsBtn) {
      clearResultsBtn.addEventListener('click', function () {
        const currentButtons = section ? Array.from(section.querySelectorAll('[data-scent-filter]')) : Array.from(document.querySelectorAll('[data-scent-filter]'));
        const clearButton = section ? section.querySelector('[data-scent-filter-clear]') : document.querySelector('[data-scent-filter-clear]');
        clearAllActive(currentButtons, clearButton, isEssentials);
        showAll(scope);
        if (isEssentials && clearButton) {
          clearButton.classList.add('essentials-filter-label--active');
          clearButton.setAttribute('aria-pressed', 'true');
        }
      });
    }
  }

  function filterByScent(scope, scent, section, isEssentials) {
    removeEmptyState(scope);
    let matchCount = 0;

    getCards(scope).forEach(function (card) {
      const raw      = card.getAttribute('data-notes') || '';
      const noteList = raw.split(',').map(function (n) { return n.trim(); }).filter(Boolean);
      const notes    = noteList.filter(function (n) { return !IGNORED_NOTES.includes(n); });

      // A card with no real note data can't be confirmed as a match, so it's hidden
      // like any other non-match rather than shown by default.
      if (notes.some(function (n) { return matchesScent(n, scent); })) {
        card.removeAttribute(HIDDEN_ATTR);
        card.style.display = '';
        matchCount++;
      } else {
        card.setAttribute(HIDDEN_ATTR, '');
        card.style.display = 'none';
      }
    });

    // Promo blocks only crowd out results when the grid is down to a
    // handful of matches, so keep them visible unless matchCount is low.
    const promoItems = (scope || document).querySelectorAll(
      '.product-item--featured-image, .product-item--promo-block, .product-item--featured-product, .essentials-cta-panel, .promo-block'
    );
    promoItems.forEach(function (item) {
      if (matchCount <= 3) {
        item.setAttribute(HIDDEN_ATTR, '');
        item.style.display = 'none';
      } else {
        item.removeAttribute(HIDDEN_ATTR);
        item.style.display = '';
      }
    });

    const paginationEls = (scope || document).querySelectorAll('.essentials-pagination, #AjaxinatePagination, .pagination');
    paginationEls.forEach(function (p) {
      p.style.display = 'none';
    });

    if (matchCount === 0) {
      showEmptyState(scope, section, isEssentials);
    }
  }

  function showAll(scope) {
    removeEmptyState(scope);
    getCards(scope).forEach(function (card) {
      card.removeAttribute(HIDDEN_ATTR);
      card.style.display = '';
    });
    const promoItems = (scope || document).querySelectorAll(
      '.product-item--featured-image, .product-item--promo-block, .product-item--featured-product, .essentials-cta-panel, .promo-block'
    );
    promoItems.forEach(function (item) {
      item.removeAttribute(HIDDEN_ATTR);
      item.style.display = '';
    });
    const paginationEls = (scope || document).querySelectorAll('.essentials-pagination, #AjaxinatePagination, .pagination');
    paginationEls.forEach(function (p) {
      p.style.display = '';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
